// src/cmps/TextEditor/nodes/EmbedNode.jsx
import React, { useEffect, useRef } from 'react'
import { DecoratorNode, createCommand } from 'lexical'

export const INSERT_SOCIAL_EMBED_COMMAND = createCommand('insert-social-embed')

export const PROVIDERS = {
  youtube: {
    re: /(?:youtu\.be\/|youtube\.com\/(?:watch\?.*v=|embed\/))([\w-]{11})/i,
    toSrc: (id) => `https://www.youtube.com/embed/${id}`,
    mode: 'iframe',
  },
  tiktok: {
    re: /tiktok\.com\/(?:@[\w.-]+\/video\/|player\/v1\/)?(\d{10,})/i,
    toSrc: (id) => `https://www.tiktok.com/player/v1/${id}`,
    mode: 'iframe',
  },
  instagram: {
    re: /instagram\.com\/(?:p|reel|tv)\/([\w-]+)/i,
    toSrc: (id) =>
      `https://www.instagram.com/${id.includes('/') ? id : `p/${id}`}/embed`,
    mode: 'iframe',
    needsScript: 'https://www.instagram.com/embed.js',
  },
  twitter: {
    re: /(?:x|twitter)\.com\/\w+\/status\/(\d+)/i,
    toSrc: (id) => id,
    mode: 'tweet',
    needsScript: 'https://platform.twitter.com/widgets.js',
  },
  facebook: {
    re: /https?:\/\/(?:www\.)?facebook\.com\/[^?#\s]+/i,
    toSrc: (url) => url.trim(),
    mode: 'fb-sdk',
    needsScript:
      'https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v19.0',
  },
}

export function ResponsiveEmbed({ provider, src }) {
  const cfg = PROVIDERS[provider] || PROVIDERS.youtube

  useEffect(() => {
    if (!cfg.needsScript) return
    if (
      document.querySelector(`script[src^="${cfg.needsScript.split('#')[0]}"]`)
    )
      return
    const s = document.createElement('script')
    s.src = cfg.needsScript
    s.async = true
    document.body.appendChild(s)
    if (provider === 'facebook' && !document.getElementById('fb-root')) {
      const root = document.createElement('div')
      root.id = 'fb-root'
      document.body.prepend(root)
    }
  }, [cfg, provider])

  if (cfg.mode === 'tweet') {
    const ref = useRef(null)
    useEffect(() => {
      const render = () =>
        window.twttr?.widgets
          ?.createTweet(src, ref.current, { dnt: true, lang: 'he' })
          .catch(() => {})
      if (window.twttr?.widgets) render()
      else {
        const id = setInterval(() => {
          if (window.twttr?.widgets) {
            clearInterval(id)
            render()
          }
        }, 300)
        return () => clearInterval(id)
      }
    }, [src])
    return (
      <div className="my-4 flex justify-center">
        <div ref={ref} />
      </div>
    )
  }

  if (cfg.mode === 'fb-sdk') {
    const ref = useRef(null)
    useEffect(() => {
      const parse = () => window.FB?.XFBML?.parse(ref.current?.parentNode)
      if (window.FB?.XFBML) parse()
      else {
        const id = setInterval(() => {
          if (window.FB?.XFBML) {
            clearInterval(id)
            parse()
          }
        }, 300)
        return () => clearInterval(id)
      }
    }, [src])
    const klass = /(?:\/video\.php\?|\/videos\/|share\/v\/)/i.test(src)
      ? 'fb-video'
      : 'fb-post'
    return (
      <div className="my-4 flex justify-center">
        <div
          ref={ref}
          className={klass}
          data-href={src}
          data-width="500"
          data-show-text="true"
        />
      </div>
    )
  }

  return (
    <div className="my-4 flex justify-center">
      <div className="relative w-full max-w-[720px] pb-[56.25%]">
        <iframe
          src={src}
          className="absolute inset-0 h-full w-full"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
          title={`${provider}-embed`}
        />
      </div>
    </div>
  )
}

function convertIframe(domNode) {
  const src = domNode.getAttribute('src') || ''
  const provider =
    Object.entries(PROVIDERS).find(([, cfg]) => cfg.re.test(src))?.[0] ||
    'youtube'
  return { node: new EmbedNode(provider, src) }
}

function convertDivEmbed(domNode) {
  const provider = domNode.getAttribute('data-embed-provider') || 'youtube'
  const src = domNode.getAttribute('data-embed-src') || ''
  return { node: new EmbedNode(provider, src) }
}

export class EmbedNode extends DecoratorNode {
  __provider
  __src

  static getType() {
    return 'social-embed'
  }

  static clone(node) {
    return new EmbedNode(node.__provider, node.__src, node.__key)
  }

  constructor(provider, src, key) {
    super(key)
    this.__provider = provider
    this.__src = src
  }

  createDOM() {
    return document.createElement('div')
  }

  updateDOM() {
    return false
  }

  decorate() {
    return (
      <div
        data-embed-provider={this.__provider}
        data-embed-src={this.__src}
        className="my-4"
      >
        <ResponsiveEmbed provider={this.__provider} src={this.__src} />
      </div>
    )
  }

  exportJSON() {
    return {
      type: 'social-embed',
      version: 1,
      provider: this.__provider,
      src: this.__src,
    }
  }

  static importDOM() {
    return {
      iframe: (domNode) => ({ conversion: convertIframe, priority: 1 }),
      div: (domNode) =>
        domNode.hasAttribute('data-embed-provider')
          ? { conversion: convertDivEmbed, priority: 2 }
          : null,
    }
  }

  exportDOM() {
    const wrapper = document.createElement('div')
    wrapper.setAttribute('data-embed-provider', this.__provider)
    wrapper.setAttribute('data-embed-src', this.__src)
    const iframe = document.createElement('iframe')
    iframe.src = this.__src
    iframe.frameBorder = '0'
    iframe.allowFullScreen = true
    wrapper.appendChild(iframe)
    return { element: wrapper }
  }

  static importJSON({ provider, src }) {
    return new EmbedNode(provider, src)
  }
}
export const $createEmbedNode = (provider, src) => new EmbedNode(provider, src)
